import React, { useEffect, useState, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { authAPI, partnerAPI } from '../utils/authUtils';
import DOMPurify from 'dompurify';
import { useInvalidateCache } from '../utils/useCache';
import { useNavigate } from 'react-router-dom';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import { handleError } from '../utils/errorHandler';
import ErrorDisplay from '../components/ErrorDisplay';
import '../styles/adminCommonStyles.css';

const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY; // Secure the API key
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB - must match backend limit

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', name: '', details: '', has_image: false });
  const [editImage, setEditImage] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', details: '' });
  const [addImage, setAddImage] = useState(null);
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [removeEditPhoto, setRemoveEditPhoto] = useState(false);
  const navigate = useNavigate();
  const { clear, clearResource } = useInvalidateCache();

  useEffect(() => {
    setIsSuperAdmin(authAPI.hasRole('superadmin'));
    setIsAdmin(authAPI.hasRole('admin'));
  }, []);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await partnerAPI.getPartners();
      const { normalizeList } = await import('../utils/responseHelpers');
      setPartners(normalizeList(response));
    } catch (err) {
      setError(handleError(err, 'Failed to fetch partners'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Create a text-only preview from HTML safely (strip tags before truncating)
  const getPreviewText = (html, max = 50) => {
    if (!html) return '';
    try {
      const tmp = html.replace(/<[^>]+>/g, '');
      const normalized = tmp.replace(/\s+/g, ' ').trim();
      return normalized.length > max ? normalized.slice(0, max) + '…' : normalized;
    } catch (e) {
      return typeof html === 'string' ? (html.slice(0, max) + (html.length > max ? '…' : '')) : '';
    }
  };

  // Success message helper
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Add partner handlers
  const handleAddInput = (e) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };
  const handleAddImage = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setAddError(`Image size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      e.target.value = '';
      return;
    }
    setAddError(null);
    setAddImage(file);
  };

  // Clean up HTML produced by WYSIWYG editors: remove inline styles/classes/ids and
  // Clean up HTML produced by WYSIWYG editors: sanitize inline styles/classes/ids and
  // unwrap non-essential tags while preserving basic formatting (bold/italic/underline/color).
  const sanitizeStyleString = (styleString) => {
    if (!styleString) return '';
    const allowedProps = ['color', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-color', 'text-decoration-line', 'text-decoration-style', 'text-align'];
    return styleString.split(';').map(s => s.trim()).filter(Boolean).map(decl => {
      const [prop, ...rest] = decl.split(':');
      if (!prop || rest.length === 0) return null;
      const name = prop.trim().toLowerCase();
      const value = rest.join(':').trim();
      if (allowedProps.includes(name)) return `${name}: ${value}`;
      return null;
    }).filter(Boolean).join('; ');
  };

  const cleanHtmlString = (rawHtml) => {
    if (!rawHtml) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, 'text/html');
      const allowedTags = new Set(['P','BR','STRONG','B','EM','I','U','UL','OL','LI','A','H1','H2','H3','H4','H5','H6','IMG','SPAN']);
      const allowClassRegex = /(^fa-|^fa$|^material-icons|^bi-|^icon-)/i;
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, null, false);
      const toProcess = [];
      while (walker.nextNode()) toProcess.push(walker.currentNode);

      toProcess.forEach(node => {
        if (node.hasAttribute) {
          try {
            const rawStyle = node.getAttribute('style');
            if (rawStyle) {
              const safeStyle = sanitizeStyleString(rawStyle);
              if (safeStyle) node.setAttribute('style', safeStyle);
              else node.removeAttribute('style');
            }
          } catch(e) {}
          try {
            const cls = node.getAttribute('class');
            if (!cls || !allowClassRegex.test(cls)) node.removeAttribute('class');
          } catch(e) {}
          try { node.removeAttribute('id'); } catch(e) {}
        }
        const tag = node.tagName.toUpperCase();
        if (!allowedTags.has(tag)) {
          const parent = node.parentNode;
          if (!parent) return;
          while (node.firstChild) parent.insertBefore(node.firstChild, node);
          parent.removeChild(node);
        } else {
          if (tag === 'A') {
            const href = node.getAttribute('href');
            Array.from(node.attributes).forEach(attr => {
              if (attr.name === 'class' && allowClassRegex.test(attr.value)) return;
              node.removeAttribute(attr.name);
            });
            if (href) node.setAttribute('href', href);
            node.setAttribute('rel','noopener noreferrer');
            node.setAttribute('target','_blank');
          } else if (tag === 'IMG') {
            const src = node.getAttribute('src');
            const alt = node.getAttribute('alt');
            Array.from(node.attributes).forEach(attr => node.removeAttribute(attr.name));
            if (src) node.setAttribute('src', src);
            if (alt) node.setAttribute('alt', alt);
          } else {
            Array.from(node.attributes).forEach(attr => {
              if (attr.name === 'class' && allowClassRegex.test(attr.value)) return;
              if (attr.name === 'style') return; // we've already sanitized style above
              node.removeAttribute(attr.name);
            });
          }
        }
      });

      const cleaned = doc.body.innerHTML || '';
      return DOMPurify.sanitize(cleaned);
    } catch (e) {
      return DOMPurify.sanitize(rawHtml.replace(/<[^>]+>/g, ''));
    }
  };
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!addForm.name || !addForm.name.trim()) {
      setAddError('Partner name is required');
      return;
    }
    
    setAddLoading(true);
    setAddError(null);
    try {
      // Sanitize details before sending to the backend
      const detailsClean = cleanHtmlString(addForm.details || '');

      // If an image was selected, send multipart FormData (backend accepts image on POST /api/partners)
      let response;
      if (addImage) {
        const form = new FormData();
        form.append('name', addForm.name.trim());
        form.append('details', detailsClean);
        form.append('image', addImage);
        response = await partnerAPI.createPartner(form, true);
      } else {
        const data = { name: addForm.name.trim(), details: detailsClean };
        response = await partnerAPI.createPartner(data);
      }
      if (response.data?.success) {
        setShowAddModal(false);
        setAddForm({ name: '', details: '' });
        setAddImage(null);
        showSuccess('Partner added successfully!');
        // Clear cached partners so public pages pick up the new partner
        try { clear('/api/partners'); } catch (e) { console.warn('Cache clear failed', e); }
        fetchPartners();
      } else {
        setAddError(response.data?.message || 'Failed to add partner');
      }
    } catch (err) {
      console.error('Partner add error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to add partner';
      setAddError(errorMsg);
    } finally {
      setAddLoading(false);
    }
  };

  // Edit partner handlers
  const handleEditClick = (partner) => {
    // Use the image endpoint when an image exists
    setEditForm({ 
      id: partner.id, 
      name: partner.name, 
      details: partner.details, 
      image_url: partner.image_url || '',
      has_image: !!partner.image_url
    });
    setEditImage(null);
    setRemoveEditPhoto(false);
    setShowEditModal(true);
    setEditError(null);
    // Scroll to edit form
    setTimeout(() => {
      scrollToForm();
    }, 0);
  };
  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleEditImage = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      setEditError(`Image size exceeds 100MB limit. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      e.target.value = '';
      return;
    }
    setEditError(null);
    setEditImage(file);
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!editForm.name || !editForm.name.trim()) {
      setEditError('Partner name is required');
      return;
    }
    
    setEditLoading(true);
    setEditError(null);
    try {
      // Clean and sanitize editor HTML before sending
      const detailsClean = cleanHtmlString(editForm.details || '');
      
      let response;
      if (editImage) {
        // If user selected a new image, update text fields first, then upload image
        const updateData = { 
          name: editForm.name.trim(), 
          details: detailsClean
        };
        const resp = await partnerAPI.updatePartner(editForm.id, updateData, false);
        
        // Step 2: Upload the new image
        try {
          const imageForm = new FormData();
          imageForm.append('image', editImage);
          await partnerAPI.uploadPartnerImage(editForm.id, imageForm);
        } catch (photoErr) {
          const photoErrMsg = photoErr.response?.data?.message || photoErr.message || 'Image upload failed';
          setEditError(photoErrMsg);
          return;  // Stop here - don't show success if image upload failed
        }
      } else if (removeEditPhoto) {
        // If user wants to remove image, send JSON-only request with removeImage flag
        const updateData = { 
          name: editForm.name.trim(), 
          details: detailsClean,
          removeImage: true
        };
        response = await partnerAPI.updatePartner(editForm.id, updateData, false);
      } else {
        // Just update text fields
        const updateData = { 
          name: editForm.name.trim(), 
          details: detailsClean
        };
        response = await partnerAPI.updatePartner(editForm.id, updateData, false);
      }
      
      // Success - update local state
      showSuccess('Partner updated successfully!');
      setShowEditModal(false);
      setEditImage(null);
      setRemoveEditPhoto(false);
      
      // Clear cache and refresh
      try { clearResource('partners', editForm.id); } catch (e) { console.warn('Cache clear failed', e); }
      fetchPartners();
    } catch (err) {
      console.error('Partner update error:', err);
      // Helpful: log backend response body (if present) for debugging
      console.error('Partner update response body:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update partner';
      setEditError(errorMsg);
    } finally {
      setEditLoading(false);
    }
  };

  // Delete partner
  const handleDeletePartner = async (partnerId, partnerName) => {
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete partners.');
      return;
    }
    const confirmDelete = window.confirm(`Are you sure you want to delete "${partnerName}"? This action cannot be undone.`);
    if (!confirmDelete) return;
    setDeleteLoading(partnerId);
    setError(null);
    try {
      await partnerAPI.deletePartner(partnerId);
      setPartners(prev => prev.filter(p => p.id !== partnerId));
      showSuccess('Partner deleted successfully!');
        try { clear('/api/partners'); } catch (e) { console.warn('Cache clear failed', e); }
    } catch (err) {
      setError(handleError(err, 'Failed to delete partner'));
    } finally {
      setDeleteLoading(null);
    }
  };

  // Logout
  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  // Permission logic
  const canEdit = isSuperAdmin || isAdmin;
  const canAdd = isSuperAdmin;
  const canDelete = isSuperAdmin;

  return (
  <div className="w-full max-w-screen-2xl mx-auto px-1 md:px-4 py-4 mt-2 landscape:px-8 landscape:py-8 flex flex-col items-center justify-center">
      <div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6 md:gap-0">
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left drop-shadow-lg">Partners</h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>{isSuperAdmin ? 'Super Admin' : isAdmin ? 'Admin' : 'User'}</span>
          <button
            onClick={handleLogout}
            className="admin-btn-danger-lg"
          >
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          <strong>Error:</strong> <ErrorDisplay error={error} className="text-red-700 font-medium" />
        </div>
      )}

      {canAdd && (
        <button
          className="mb-6 px-6 py-3 bg-blue-600 text-white rounded-xl hover:scale-105 shadow-lg transition-all duration-300 border border-blue-700 font-medium"
          onClick={() => {
            setShowAddModal(true);
            setTimeout(() => {
              scrollToForm();
            }, 0);
          }}
        >
          + Add New Partner
        </button>
      )}

      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto admin-modal-form" id="add-partner-form">
            <h3 className="text-xl font-bold mb-4">Add New Partner</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Details</label>
                <Editor
                  value={addForm.details}
                  onEditorChange={val => setAddForm(f => ({ ...f, details: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 240,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    telemetry_opt_out: true,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    content_css: ['https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css'],
                    content_style: 'a{color:#1e3a8a; text-decoration:underline;} img{max-width:100%;}',
                    extended_valid_elements: 'i[class],span[class],svg[*],path[*]',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              {/* Image Upload Section - mirrored from Edit form */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload New Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAddImage}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
              </div>

              {addImage && (
                <div className="mb-3 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                  <p className="text-sm font-medium text-[#3a8b9e] mb-2">New Photo Preview:</p>
                  <img
                    src={URL.createObjectURL(addImage)}
                    alt="New Preview"
                    className="h-32 w-32 object-cover rounded-lg"
                  />
                </div>
              )}
              {addError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {addError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddError(null);
                  }}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary"
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding...' : 'Add Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 flex items-start justify-center bg-black bg-opacity-50 z-50 overflow-y-auto pt-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl admin-modal-form" id="edit-partner-form">
            <h3 className="text-xl font-bold mb-4">Edit Partner</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="admin-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Details</label>
                <Editor
                  value={editForm.details}
                  onEditorChange={val => setEditForm(f => ({ ...f, details: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 240,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    telemetry_opt_out: true,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    content_css: ['https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css'],
                    content_style: 'a{color:#1e3a8a; text-decoration:underline;} img{max-width:100%;}',
                    extended_valid_elements: 'i[class],span[class],svg[*],path[*]',
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>

              {/* Image Upload Section */}
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Photo</label>
                {editForm.has_image && !removeEditPhoto && (
                  <div className="mb-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 mb-2">Current Photo:</p>
                    <img
                      src={(() => {
                        // Handle various URL formats
                        if (!isNaN(Number(editForm.image_url))) {
                          return `${API_BASE}/image/${editForm.image_url}?t=${Date.now()}`;
                        }
                        if (editForm.image_url && editForm.image_url.startsWith('/images/')) {
                          return editForm.image_url + `?t=${Date.now()}`;
                        }
                        if (editForm.image_url && editForm.image_url.startsWith('/api/')) {
                          return `${API_BASE}${editForm.image_url}?t=${Date.now()}`;
                        }
                        if (editForm.image_url && editForm.image_url.startsWith('/')) {
                          return `${API_BASE}${editForm.image_url}?t=${Date.now()}`;
                        }
                        return editForm.image_url;
                      })()}
                      alt="Current"
                      className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      className="mt-3 admin-btn-danger"
                      onClick={() => setRemoveEditPhoto(true)}
                      disabled={editLoading}
                    >
                      Remove Photo
                    </button>
                  </div>
                )}
                {removeEditPhoto && (
                  <div className="mb-3 p-4 bg-red-50 border border-red-300 rounded-lg">
                    <p className="text-sm font-medium text-red-700 mb-2">Photo will be removed</p>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => setRemoveEditPhoto(false)}
                      disabled={editLoading}
                    >
                      Keep Photo
                    </button>
                  </div>
                )}
                {!removeEditPhoto && (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload New Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditImage}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                    {editImage && (
                      <div className="mt-3 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                        <p className="text-sm font-medium text-[#3a8b9e] mb-2">New Photo Preview:</p>
                        <img
                          src={URL.createObjectURL(editImage)}
                          alt="New Preview"
                          className="h-32 w-32 object-cover rounded-lg border border-blue-300"
                        />
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">Leave empty to keep the current photo</div>
                  </>
                )}
              </div>

              {editError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-success"
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Loading partners..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
  <div className="admin-table-container">
          <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Image</th>
                <th className="admin-table-header-cell">Name</th>
                <th className="admin-table-header-cell">Details</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {partners.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-8 px-4 text-center text-gray-500">
                    <div className="text-lg">No partners found</div>
                    <div className="text-sm mt-1">Add your first partner to get started!</div>
                  </td>
                </tr>
              ) : (
                partners.map((partner) => (
                  <tr key={partner.id} className="hover:bg-blue-50 transition-colors">
                    <td className="py-4 px-4">
                      {partner.image_url ? (
                        <img
                          src={partner.image_url.startsWith('/api/') ? `${API_BASE}${partner.image_url}?t=${Date.now()}` : partner.image_url}
                          alt={partner.name}
                          className="h-16 w-16 object-cover rounded-lg shadow-sm"
                          onError={(e) => {
                            console.log('Image failed to load');
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                          No Image
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4 font-medium text-gray-900">
                      {partner.name && partner.name.length > 50 ? partner.name.slice(0, 50) + '…' : partner.name}
                    </td>
                    <td className="py-4 px-4 max-w-xs truncate whitespace-pre-line break-words" title={partner.details}>
                      <div className="text-sm text-slate-700">
                        {getPreviewText(partner.details)}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-2 items-stretch justify-center">
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(partner)}
                            className="admin-btn-table-edit">
                            Edit
                          </button>
                        )}
                        {canDelete ? (
                          <button
                            onClick={() => handleDeletePartner(partner.id, partner.name)}
                            disabled={deleteLoading === partner.id}
                            className="admin-btn-table-danger">
                            {deleteLoading === partner.id ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="admin-btn-table-disabled"
                            title="Superadmin role required to delete partners">
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
